import React, { Fragment } from 'react';
import styled from 'styled-components';

import { LayoutColors } from '@src/core/theme';
import { CartCouponTypeEnum, Order } from '@src/core/buyerApiClient';
import { device } from '@src/common/breakpoints';
import { FiDownloadCloud } from 'react-icons/fi';
import OrderStatusDisplay from '@src/components/ui/OrderStatusDisplay';
import { formatCents, formatDateTime } from '@src/common';
import { BodyLargeBold, BodyRegular, BodyRegularSemiBold, BodySmall, Heading6 } from '@src/fonts/typography/Typography';
import { useTranslate } from '@src/core/hooks';

import { baseButton } from '@src/components/Account/shared';
import DotFalling from '@src/components/reusable/spinner/DotFalling';
import { Tooltip } from '@src/components/reusable/Tooltip';
import { useOrderDetailsHeader } from '@src/features/orders/details';
import { useVoucherDownload } from '@src/features/vouchers';
import { ServiceFeeDisplay } from '@src/components/reusable/ServiceFee';

type Props = {
  order: Order;
  originalAmountCents?: number;
};

const HeaderContent = ({ order, originalAmountCents }: Props) => {
  const { i18n, i18nElement } = useTranslate();

  const {
    coupon: saleCoupon,
    saleChangeAmounts,
    serviceFeeDescription,
    hasMultipleVouchers,
    hasPendingVouchers,
  } = useOrderDetailsHeader(order);
  const { download, isDownloading } = useVoucherDownload();

  const loadingVouchersPdf = isDownloading(order.id);
  const handleOpenVouchers = () => download({ kind: 'order', id: order.id });

  return (
    <Container>
      <HeaderMainInfo>
        <AccountNumberWrapper>
          <h3>
            {i18nElement('account_sellNumber', {
              orderCode: order.code,
            })}
          </h3>

          {hasPendingVouchers ? (
            <VoucherStatus>{i18n('account_sale_voucher_pending')}</VoucherStatus>
          ) : (
            <OrderStatusDisplay status={order.status} />
          )}
        </AccountNumberWrapper>

        {hasMultipleVouchers ? (
          <Tooltip
            arrow
            placement='top'
            showContent={hasPendingVouchers}
            label={i18n('account_sale_download_all_vouchers_tooltip')}
          >
            <Button onClick={() => handleOpenVouchers()} disabled={hasPendingVouchers || loadingVouchersPdf}>
              {loadingVouchersPdf ? (
                <DotFalling />
              ) : (
                <>
                  <FiDownloadCloud color={LayoutColors.whiteLight} size={22} />
                  {i18n('account_sale_download_all')}
                </>
              )}
            </Button>
          </Tooltip>
        ) : null}
      </HeaderMainInfo>

      {hasPendingVouchers ? (
        <PendingVoucherInfo>{i18nElement('account_sale_pending_voucher_info')}</PendingVoucherInfo>
      ) : null}

      <Details>
        <div>
          <span>{i18n('account_sale_date')}:</span>
          <span>{formatDateTime(order.createdAt)}</span>
        </div>
        {saleCoupon ? (
          <div>
            <span>{i18n('account_sale_discount')}:</span>
            <p>
              {saleCoupon?.type === CartCouponTypeEnum.Percentage ? (
                <span>{`(-${saleCoupon.formattedDiscount})`}</span>
              ) : null}

              <span>{formatCents(saleCoupon.fullAmountCents - saleCoupon.finalAmountCents)}</span>
            </p>
          </div>
        ) : null}

        {saleChangeAmounts.length > 0 ? (
          <Fragment>
            {originalAmountCents !== undefined ? (
              <div>
                <span>{i18n('account_sale_original_total_value')}:</span>
                <span>{formatCents(originalAmountCents)}</span>
              </div>
            ) : null}

            {saleChangeAmounts.map(([saleChangeId, amount]) => (
              <div key={saleChangeId}>
                <SaleChangeAmountLabel>{i18n('account_sale_change_amount')}</SaleChangeAmountLabel>
                <SaleChangeAmountValue>+ {formatCents(amount)}</SaleChangeAmountValue>
              </div>
            ))}
          </Fragment>
        ) : null}

        <ServiceFeeDisplay
          serviceFeeCurrencyInfo={order.serviceFeeCurrencyInfo}
          serviceFeeDescription={serviceFeeDescription}
        />

        <div>
          <span>{i18n('account_sale_total_value')}:</span>
          <strong>{formatCents(order.finalAmountCents)}</strong>
        </div>
      </Details>
    </Container>
  );
};

export default HeaderContent;

const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderMainInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${device.mediumTablet} {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
  }
`;

const AccountNumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  h3 {
    ${BodyRegularSemiBold};
    color: ${LayoutColors.blackMain};
  }

  ${device.mediumTablet} {
    gap: 22px;

    h3 {
      ${Heading6};
    }
  }
`;
const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;

  padding-bottom: 16px;
  border-bottom: 1px solid ${LayoutColors.grayMain};

  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    span {
      ${BodySmall};
      color: ${LayoutColors.blackMain};
    }

    p {
      display: flex;
      flex-direction: row;
      gap: 8px;
      align-items: center;
    }

    strong {
      ${BodyRegularSemiBold};
      color: ${LayoutColors.blackMain};
    }
  }

  ${device.mediumTablet} {
    border-bottom: none;

    > div {
      span {
        ${BodyRegular};
      }

      strong {
        ${BodyLargeBold};
      }
    }
  }
`;

const Button = styled.button`
  ${baseButton};
  text-transform: uppercase;

  color: ${LayoutColors.whiteLight};
  background: ${LayoutColors.justBlue};

  ${device.smallTablet} {
    min-width: 190px;
    height: 44px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const VoucherStatus = styled.p`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  background: ${LayoutColors.warningDark};
  border-radius: 8px;

  ${BodySmall};
  color: ${LayoutColors.whiteLight};
  padding: 8px;
`;

const PendingVoucherInfo = styled.p`
  width: 100%;
  max-width: 504px;

  ${BodyRegular}
  color: ${LayoutColors.blackMain};
`;

const SaleChangeAmountLabel = styled.span`
  @media (max-width: 768px) {
    width: 100%;
    max-width: 210px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SaleChangeAmountValue = styled.span`
  white-space: nowrap;
  text-align: right;
`;
